import { useState, useEffect, useRef } from "react";
import { FiMenu, FiX, FiChevronDown } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/techeco_logo.png";
import { QrCodeIcon } from "@heroicons/react/24/outline";

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);
  const navigate = useNavigate(); // Hook for navigation

  const navigation = [
    {
      name: "Services",
      submenu: [
        {
          name: "Technology",
          icon: "💻",
          path: "/technology-services",
          submenu: [
            { name: "Web Development", path: "/technology-services#web", description: "Custom solutions using modern frameworks" },
            { name: "Software Development", path: "/technology-services#software-development", description: "Scalable software solutions for businesses" },
            { name: "Cloud Solutions", path: "/technology-services#cloud-solutions", description: "AWS, Azure, and Google Cloud services" },
            { name: "Mobile App Development", path: "/technology-services#mobile-app", description: "iOS and Android native & cross-platform" },
            { name: "Data Analytics", path: "/technology#data-analytics", description: "Data-driven insights" },
          ],
        },
        {
          name: "Digital Marketing",
          icon: "📢",
          path: "/digital-marketing-services",
          submenu: [
            { name: "SEO Optimization", path: "/digital-marketing-services#seo-optimization", description: "Boost your search rankings with proven strategies" },
            { name: "Social Media Marketing", path: "/digital-marketing-services#social-media", description: "Engage and grow your audience on social platforms" },
            { name: "Product Photography", path: "/digital-marketing-services#product-photography", description: "High-quality images to showcase your products effectively" },
            { name: "Website Maintenance", path: "/digital-marketing-services#website-maintenance", description: "Ensure optimal website performance and updates" },
          ],
        },
        {
          name: "UI/UX Design",
          icon: "🎨",
          path: "/ui-ux-design-services",
          submenu: [
            { name: "Website UI/UX Design", path: "/ui-ux-design-services#ui-ux", description: "Design engaging and user-friendly websites" },
            { name: "Wireframing & Prototyping", path: "/ui-ux-design-services#wireframing", description: "Create website mockups and interactive prototypes" },
            { name: "E-Commerce UI/UX", path: "/ui-ux-design-services#ecommerce-ui", description: "Enhance online store design for better conversions" },
            { name: "Accessibility Design", path: "/ui-ux-design-services#accessibility", description: "Ensure inclusive and accessible website experiences" },
          ],
        },
      ],
    },
    { name: "Who We Are", path: "/whoweare" },
    { 
      name: (
        <div className="flex flex-col items-center ">
          <span className="text-sm">Call us anytime</span>
          <span className="flex items-center">
            🎧 +91 9483085077
          </span>
        </div>
      ), 
      path: "tel:+91 9483085077", 
      isMobile: true 
    },
    { name: "QR", path: "/qr-code-generator-tes", icon: <QrCodeIcon className="w-5 h-5 mr-2" /> },
    // { name: "News", path: "/news" },
  ];

  const mobileNavItems = [
  ...navigation,
  { name: "Contact Us", path: "/contact-tes" }
];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setOpenDropdown(null);
  };

  const handleDesktopDropdown = (itemName) => {
    setOpenDropdown(openDropdown === itemName ? null : itemName);
  };

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setOpenDropdown(null); // Close dropdown
    setIsMobileMenuOpen(false); // Close mobile menu if open
  };

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  return (
    <nav ref={navRef} className="fixed w-full z-50 bg-black  backdrop-blur-lg">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex justify-between h-24 items-center">
          <Link to="/">
            <img className="h-16 w-auto" src={logo} alt="Company Logo" />
          </Link>

          <div className="hidden md:flex items-center gap-5 flex-1 justify-center relative">
            {navigation.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.submenu && handleDesktopDropdown(item.name)}
                onMouseLeave={() => item.submenu && handleDesktopDropdown(null)}
              >
                {item.submenu ? (
                  <button className="text-lg text-white flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-md">
                    {item.name}
                    <FiChevronDown className="text-xl" />
                  </button>
                ) : (
                  <Link to={item.path} className="text-lg text-white px-4 py-2 hover:bg-white/10 rounded-md flex items-center">
                    {item.icon && item.icon}
                    {item.name}
                  </Link>
                )}
                {item.submenu && openDropdown === item.name && (
                  <div className="absolute top-full left-[-320px] w-[70vw] bg-[#656464] backdrop-blur-lg p-2 shadow-lg rounded-lg">
                    <div className="max-w-7xl mx-auto flex flex-wrap gap-24">
                      {item.submenu.map((category) => (
                        <div key={category.name} className="text-white w-1/4">
                     <button
                        onClick={() => handleNavigation(category.path)}
                        className="flex items-center mb-2 text-lg font-semibold transition-transform duration-200 hover:scale-105 hover:text-blue-500"
                      >
                        <span className="mr-2 text-xl">{category.icon}</span>
                        {category.name}
                      </button>
                          <div className="space-y-1">
                            {category.submenu.map((service) => (
                              <Link
                              key={service.name}
                              to={service.path}
                              className="block p-3 text-sm bg-black hover:bg-white/20 rounded-md transition"
                              onClick={() => handleNavigation(service.path)} // Close dropdown when clicked
                            >
                              <div className="font-bold">{service.name}</div>
                              <p className="text-xs text-white/70">{service.description}</p>
                            </Link>
                            
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="hidden md:block lg:mr-[-50px]">
            <Link to="/contact-tes" className="text-lg text-white border border-white rounded-full px-6 py-2 hover:bg-white/10 transition">
              Contact Us
            </Link>
          </div>

          <div className="md:hidden">
            <button onClick={toggleMobileMenu} className="text-white p-3 hover:bg-white/10 rounded-md">
              {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-black border-t border-white/20 max-h-[calc(100vh-4rem)] overflow-y-auto">
          {mobileNavItems.map((item, index) => (
            <div key={item.name} className="border-b border-white/20">
              {item.submenu ? (
                <>
                  <button
                    onClick={() => toggleDropdown(index)}
                    className="w-full text-left px-5 py-4 text-lg text-white hover:bg-white/10 flex justify-between items-center"
                  >
                    {item.name}
                    <FiChevronDown className={`ml-2 transition-transform ${openDropdown === index ? "rotate-180" : ""}`} />
                  </button>
                  {openDropdown === index && (
                    <div className="bg-gray-800 pl-6">
                      {item.submenu.map((sub) => (
                        <Link
                        key={sub.name}
                        to={sub.path}
                        className="block px-5 py-3 text-white hover:bg-gray-700 transition duration-200"
                        onClick={() => handleNavigation(sub.path)} // Close menu when clicked
                      >
                        {sub.name}
                      </Link>
                      
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link to={item.path} onClick={handleMobileNavClick} className="block w-full text-left px-5 py-4 text-lg text-white hover:bg-white/10 flex items-center">
                  {item.icon && item.icon}
                  {item.name}
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </nav>
  );
}
